import { toDateShort } from '@monetize/utils/core';
import { useMemo } from 'react';
import { MFlex, MTag, MTagCloseButton, MTagLabel } from '../chakra';
import MButton from '../MButton';
import { MTooltip } from '../MTooltip';
import { AppTable } from './appTable';
import {
  DateFilterComparison,
  DateFilterData,
  NumberFilterComparison,
  NumberFilterData,
} from './DataTableTypes';
import { getColumnFilterKey, TableFilterMap } from './tableUtilsNew';

interface ActiveFilter {
  columnId: string;
  columnHeader: string;
  label: string;
  tooltipLabel?: string;
}

export interface ExtraActiveFilter {
  key: string;
  columnHeader: string;
  label?: string;
  tooltipLabel?: string;
  onRemove: () => void;
}

interface DataTableActiveFiltersProps {
  table: AppTable<any>;
  columnFilters: Map<string, unknown>;
  onRemoveFilter: (columnId: string) => void;
  onResetAll?: () => void;
  extraFilters?: ExtraActiveFilter[];
}

const MAX_SET_VALUES_SHOWN = 3;

function getColumnHeaderText(table: AppTable<any>, columnId: string): string {
  const column = table.getColumn(columnId);
  const header = column?.columnDef.header;
  if (typeof header === 'string') {
    return header;
  }
  return columnId;
}

function getSetFilterLabel(
  value: Set<string | number | boolean>,
  filterValues: TableFilterMap | undefined,
): { label: string; tooltip: string } {
  if (value.size === 0) {
    return { label: '', tooltip: '' };
  }

  // Build a lookup map from value to label
  const labelMap = new Map<string | number | boolean, string>();
  if (filterValues) {
    filterValues.forEach((fv) => labelMap.set(fv.value, fv.label));
  }

  const labels = Array.from(value).map((v) => labelMap.get(v) ?? String(v));

  if (labels.length <= MAX_SET_VALUES_SHOWN) {
    return { label: labels.join(', '), tooltip: labels.join(', ') };
  }
  return { label: `${labels.length} selected`, tooltip: labels.join(', ') };
}

function getDateFilterLabel(value: DateFilterData): string {
  const { comparison, firstDate, secondDate } = value;

  switch (comparison) {
    case DateFilterComparison.EQUALS:
      return `Equals ${toDateShort(firstDate ?? null)}`;
    case DateFilterComparison.GREATER_THAN:
      return `After ${toDateShort(firstDate ?? null)}`;
    case DateFilterComparison.GREATER_THAN_EQUAL:
      return `On or after ${toDateShort(firstDate ?? null)}`;
    case DateFilterComparison.LESS_THAN:
      return `Before ${toDateShort(firstDate ?? null)}`;
    case DateFilterComparison.LESS_THAN_EQUAL:
      return `On or before ${toDateShort(firstDate ?? null)}`;
    case DateFilterComparison.BETWEEN_EXCLUSIVE:
    case DateFilterComparison.BETWEEN_INCLUSIVE:
      return `${toDateShort(firstDate ?? null)} - ${toDateShort(secondDate ?? null)}`;
    default:
      return '';
  }
}

function getNumberFilterLabel(value: NumberFilterData): string {
  const { comparison, firstValue, secondValue } = value;

  switch (comparison) {
    case NumberFilterComparison.IS_BLANK:
      return 'Is Blank';
    case NumberFilterComparison.IS_NOT_BLANK:
      return 'Is Not Blank';
    default:
      break;
  }

  if (!firstValue) {
    return '';
  }

  switch (comparison) {
    case NumberFilterComparison.EQUALS:
      return `Equals ${firstValue}`;
    case NumberFilterComparison.GREATER_THAN:
      return `> ${firstValue}`;
    case NumberFilterComparison.GREATER_THAN_EQUAL:
      return `>= ${firstValue}`;
    case NumberFilterComparison.LESS_THAN:
      return `< ${firstValue}`;
    case NumberFilterComparison.LESS_THAN_EQUAL:
      return `<= ${firstValue}`;
    case NumberFilterComparison.BETWEEN_EXCLUSIVE:
    case NumberFilterComparison.BETWEEN_INCLUSIVE:
      return secondValue ? `${firstValue} - ${secondValue}` : '';
    default:
      return '';
  }
}

function getBooleanFilterLabel(value: string): string {
  if (value === 'CHECKED') {
    return 'Yes';
  }
  if (value === 'UNCHECKED') {
    return 'No';
  }
  return '';
}

export function DataTableActiveFilters({
  table,
  columnFilters,
  onRemoveFilter,
  onResetAll,
  extraFilters,
}: Readonly<DataTableActiveFiltersProps>) {
  const activeFilters = useMemo(() => {
    const filters: ActiveFilter[] = [];

    columnFilters.forEach((value, columnId) => {
      const column = table.getColumn(columnId);
      if (!column) {
        return;
      }

      const filterVariant = column.columnDef.meta?.filterVariant;
      const headerText = getColumnHeaderText(table, columnId);
      const key = getColumnFilterKey(column);

      switch (filterVariant) {
        case 'SET': {
          const filterValues = table.options.meta?.filterValues?.get(key);
          const allSelected =
            !!filterValues &&
            filterValues.length > 0 &&
            value instanceof Set &&
            filterValues.every(({ value: filterValue }) =>
              value.has(filterValue),
            );
          if (value instanceof Set && value.size > 0 && !allSelected) {
            const { label, tooltip } = getSetFilterLabel(value, filterValues);
            filters.push({
              columnId,
              columnHeader: headerText,
              label,
              tooltipLabel: tooltip,
            });
          }
          break;
        }
        case 'BOOLEAN': {
          if (typeof value === 'string' && value !== 'ALL') {
            const valueLabel = getBooleanFilterLabel(value);
            filters.push({
              columnId,
              columnHeader: headerText,
              label: valueLabel,
            });
          }
          break;
        }
        case 'DATE': {
          if (
            value != null &&
            typeof value === 'object' &&
            'comparison' in value
          ) {
            const valueLabel = getDateFilterLabel(value as DateFilterData);
            if (valueLabel) {
              filters.push({
                columnId,
                columnHeader: headerText,
                label: valueLabel,
              });
            }
          }
          break;
        }
        case 'SELECTION': {
          if (typeof value === 'string' && value !== 'ALL') {
            filters.push({
              columnId,
              columnHeader: headerText,
              label: `${value === 'SELECTED' ? 'Selected' : 'Unselected'}`,
            });
          }
          break;
        }
        case 'OWNER': {
          if (
            value != null &&
            typeof value === 'object' &&
            'ownerName' in value
          ) {
            const { ownerName } = value as { ownerName: string };
            if (ownerName) {
              filters.push({
                columnId,
                columnHeader: headerText,
                label: ownerName,
              });
            }
          }
          break;
        }
        case 'RECORD_SEARCH': {
          if (
            value != null &&
            typeof value === 'object' &&
            'id' in value &&
            'label' in value
          ) {
            const { id, label } = value as { id: string; label: string };
            if (id) {
              filters.push({
                columnId,
                columnHeader: headerText,
                label: label || id,
              });
            }
          }
          break;
        }
        case 'NUMBER': {
          if (
            value != null &&
            typeof value === 'object' &&
            'comparison' in value
          ) {
            const valueLabel = getNumberFilterLabel(value as NumberFilterData);
            if (valueLabel) {
              filters.push({
                columnId,
                columnHeader: headerText,
                label: valueLabel,
              });
            }
          }
          break;
        }
        case 'TEXT': {
          if (typeof value === 'string' && !!value.trim()) {
            filters.push({
              columnId,
              columnHeader: headerText,
              label: value.trim(),
            });
          }
          break;
        }
        default:
          break;
      }
    });

    return filters;
  }, [columnFilters, table]);

  if (activeFilters.length === 0 && !extraFilters?.length) {
    return null;
  }

  return (
    <MFlex flexWrap="wrap" gap={2} py={2} alignItems="center">
      {activeFilters.map(({ columnId, columnHeader, label, tooltipLabel }) => (
        <MTooltip
          key={columnId}
          label={tooltipLabel}
          contentProps={{ fontSize: 'xs' }}
        >
          <MTag
            key={columnId}
            variant="blue"
            size="sm"
            borderRadius="full"
            py={1}
          >
            <MTagLabel fontSize="xs" fontWeight="bold" mr={1}>
              {columnHeader}:
            </MTagLabel>
            <MTagLabel fontSize="xs">{label}</MTagLabel>
            <MTagCloseButton onClick={() => onRemoveFilter(columnId)} />
          </MTag>
        </MTooltip>
      ))}
      {extraFilters?.map(
        ({ key, columnHeader, label, tooltipLabel, onRemove }) => (
          <MTooltip
            key={key}
            label={tooltipLabel}
            contentProps={{ fontSize: 'xs' }}
          >
            <MTag variant="blue" size="sm" borderRadius="full" py={1}>
              <MTagLabel fontSize="xs" fontWeight="bold" mr={label ? 1 : 0}>
                {columnHeader}
                {label ? ':' : ''}
              </MTagLabel>
              {label && <MTagLabel fontSize="xs">{label}</MTagLabel>}
              <MTagCloseButton onClick={onRemove} />
            </MTag>
          </MTooltip>
        ),
      )}
      <MButton
        variant="link"
        size="xs"
        onClick={() => {
          onResetAll?.();
        }}
        ml={1}
      >
        Reset All
      </MButton>
    </MFlex>
  );
}
